import type { ItemStockRow } from "@/features/staff/item-stock/item-stock"
import { intlLocale, t } from "@/lib/i18n"

/**
 * Shared shape, endpoints and formatting for the till screen (`/shifts`).
 *
 * **The browser never re-derives a figure.** Every amount in the payload is an
 * exact decimal string computed by `Staff::ShiftReportQuery` in integer cents;
 * this module formats those strings and does not add, subtract or scale them.
 * The rule is not fussiness: a Z report a cashier signs and a number the
 * browser derived have to be the same number, and two implementations of the
 * same sum drift the first time a rounding rule changes.
 *
 * There is exactly one exception, and it is deliberate: `cashDifference`, the
 * preview inside the close ceremony. The counted cash is a number the cashier
 * just typed and the server has never seen, so no server figure can describe it
 * until the close is submitted. Showing the consequence of their own input
 * before they commit to it is the entire point of asking for confirmation. It
 * is one subtraction, over an exact decimal string, rounded to cents, and it is
 * unit tested. Once the shift is closed the server's own `difference` is what
 * renders — this preview never outlives the dialog it lives in.
 */

export type ShiftStatus = "open" | "closed"

export type ShiftIdentity = {
  id: number
  status: ShiftStatus
  openedAt: string
  closedAt: string | null
  openedBy: string | null
  closedBy: string | null
}

export type MethodTotal = {
  paymentMethod: string
  paymentMethodLabel: string
  count: number
  subtotal: string
  tip: string
  dinerFee: string
  collected: string
}

export type CashMovementRow = {
  id: number
  kind: string
  kindLabel: string
  reason: string
  reasonLabel: string
  note: string | null
  amount: string
  signedAmount: string
  actor: string | null
  createdAt: string
}

/**
 * `itemized` carries lines; the other two carry none and mean different things.
 * Rendering either of them as "0.00" would be a filing error waiting to happen,
 * which is why the server names the case instead of sending a zero.
 */
export type TaxStatus = "itemized" | "forward_regime" | "not_recorded"

export type ShiftReport = {
  shift: ShiftIdentity
  timeZone: string
  startsAt: string | null
  endsAt: string | null
  transactionCount: number
  /** Bills that would refuse the close. `null` once the shift is closed. */
  blockingBillCount: number | null
  subtotal: string
  tip: string
  dinerFee: string
  openingFloat: string
  cashSales: string
  movementsTotal: string
  expectedCash: string
  /** Both `null` until the shift is closed. A counted-empty drawer is not the same as uncounted. */
  declaredCash: string | null
  difference: string | null
  paymentMethods: MethodTotal[]
  movements: CashMovementRow[]
  /** The same movements rolled up by kind and reason. What a signed Z lists. */
  movementGroups: {
    kind: string
    reason: string
    kindLabel: string
    reasonLabel: string
    count: number
    amount: string
  }[]
  /**
   * What was given back. `drawerTotal` is ALREADY inside `movementsTotal` and
   * therefore inside `expectedCash` — a cash refund is written as a `cash_out`
   * movement, which is exactly why the drawer reconciles. This block names that
   * money; it is not a second deduction, and nothing here may be subtracted
   * from a figure above.
   *
   * The payload also carries a per-refund row list. The panel renders the
   * roll-up instead, for the same reason it rolls up cash movements: at close
   * the question is what left and why, not in which order.
   */
  refunds: {
    count: number
    total: string
    drawerCount: number
    drawerTotal: string
    providerCount: number
    providerTotal: string
    groups: {
      rail: string
      reason: string
      reasonLabel: string
      count: number
      amount: string
    }[]
  }
  /**
   * Money the restaurant chose not to collect.
   *
   * **Already deducted.** A discount reduces the line total before the payment
   * attempt is written, so `subtotal`, `cashSales` and every rail above are net
   * of it. This block exists to NAME the giveaway, never to subtract it a
   * second time.
   */
  discounts: {
    count: number
    total: string
    /** A discounted sale is still a sale. */
    revenueTotal: string
    /** A courtesy never was one: product left without being sold. */
    nonRevenueTotal: string
    groups: {
      kind: string
      kindLabel: string
      reason: string
      reasonLabel: string
      countsAsRevenue: boolean
      count: number
      amount: string
    }[]
  }
  /** Rung up and taken back off the floor, at menu value. Also not a deduction. */
  cancellations: {
    count: number
    total: string
    billCount: number
    orderCount: number
    groups: {
      scope: string
      scopeLabel: string
      reason: string | null
      reasonLabel: string
      count: number
      amount: string
    }[]
  }
  tax: {
    status: TaxStatus
    total: string
    attemptsWithoutBreakdown: number
    lines: { name: string; rate: string; amount: string }[]
  }
  outOfShift: {
    count: number
    subtotal: string
    tip: string
    collected: string
    paymentMethods: MethodTotal[]
  }
}

export type TillOption = {
  value: string
  label: string
}

/** Every write the screen performs. See `config/routes.rb` staff location block. */
export const TILL_PATHS = {
  open: "/shifts",
  close: (shiftId: number) => `/shifts/${shiftId}`,
  movements: "/cash_movements",
  // The till's own stock route, not /manage's. Same records, different door:
  // that one is manager-only setup, this one carries a single count and needs
  // a drawer open. See Staff::Location::ShiftItemStocksController.
  itemStock: (itemStockId: string) => `/shifts/item_stocks/${itemStockId}`,
  manageItemStocks: "/manage/item_stocks",
} as const

const I18N_PREFIX = "ui.react.pages.staff.location.shifts.show"

export function tTill(key: string, options: Record<string, string | number> = {}) {
  return t(`${I18N_PREFIX}.${key}`, options)
}

/**
 * A tax rate arrives as the fraction `Location::TaxSnapshot` froze ("0.16").
 * Formatting it as a percentage is a display concern, the same class of
 * operation as putting a currency symbol on an amount — no figure is being
 * derived. An unparseable rate renders verbatim rather than as "NaN%".
 */
export function formatRate(rate: string) {
  const numeric = toNumber(rate)
  if (numeric === null) return rate

  try {
    return new Intl.NumberFormat(intlLocale(), {
      style: "percent",
      maximumFractionDigits: 2,
    }).format(numeric)
  } catch {
    return rate
  }
}

/**
 * `null` for anything that is not a number, blank included. `Number("")` is 0
 * in JavaScript, and "the field is empty" is not the same answer as "the drawer
 * held nothing" — on this screen the second one is a signable statement.
 */
function toNumber(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === "") return null

  const numeric = Number(trimmed)
  return Number.isFinite(numeric) ? numeric : null
}

/** `null` for an empty or unusable field, so a blank input reads as "not counted yet". */
export function parseCountedCash(raw: string): number | null {
  return toNumber(raw)
}

/**
 * Counted minus expected, rounded to cents so binary-float dust never surfaces
 * as `-0.000000001` in front of a cashier. `null` means "cannot say yet":
 * nothing has been counted, or the expected amount is unreadable.
 */
export function cashDifference(expectedCash: string, countedRaw: string): number | null {
  const expected = toNumber(expectedCash)
  if (expected === null) return null

  const counted = toNumber(countedRaw)
  if (counted === null) return null

  return Math.round((counted - expected) * 100) / 100
}

export type DifferenceTone = "balanced" | "over" | "short"

export function differenceTone(difference: number): DifferenceTone {
  if (difference === 0) return "balanced"
  return difference > 0 ? "over" : "short"
}


// ── The day around the drawer ────────────────────────────────────────────────
//
// The till screen answers three questions, in the order the person opening the
// register asks them: how the last shift was left, which hours the restaurant
// keeps today, and what it is starting the day with. The two blocks below carry
// the first two; `ItemStockRow` (imported, not redeclared) carries the third.

/**
 * Answered on the server by `Staff::LocationJourneyQuery`, never re-derived
 * here. Comparing a browser clock against a period list is a second definition
 * of "abierto", and it disagrees with the first one the day an overnight period
 * or a DST weekend is involved.
 */
export type JourneyStatus = "open" | "opens_later" | "closed_today" | "not_configured" | "disabled"

export type ShiftJourney = {
  /** The restaurant's local date, ISO. */
  date: string
  timeZone: string
  status: JourneyStatus
  /** Only the periods that START today; minutes from midnight, as stored. */
  periods: { startsAt: number; endsAt: number; overnight: boolean }[]
  /** Set when `status` is `opens_later`. */
  opensAt: string | null
  /** Set when `status` is `open`. */
  closesAt: string | null
}

/**
 * The last signed close, in the figures someone starting a day asks about.
 * Read off the shift row, which froze them at close — this is never the whole
 * X/Z projection, and the page offers the full report only when it holds one.
 */
export type ShiftCloseSummary = {
  id: number
  openedAt: string
  closedAt: string | null
  openedBy: string | null
  closedBy: string | null
  openingFloat: string
  expectedCash: string | null
  declaredCash: string | null
  difference: string | null
}

/**
 * `null` at a restaurant that does not count dishes — which is not the same as
 * one that counts none yet, and gets a different screen. `stockCount` describes
 * the OPEN shift's arqueo, and is null for a shift opened before the feature
 * was switched on: claiming it was counted would be inventing a signature.
 */
export type TillItemStock = {
  stocks: ItemStockRow[]
  stockCount: { entries: number; edited: number } | null
}

/**
 * Whole minutes from now until an instant, or `null` when there is nothing to
 * count down to — no instant, an unreadable one, or one that has already
 * passed. A countdown that has run out says nothing; the status beside it
 * already does.
 */
export function minutesUntil(iso: string | null, now: number = Date.now()): number | null {
  if (!iso) return null

  const target = new Date(iso).getTime()
  if (Number.isNaN(target)) return null

  const minutes = Math.floor((target - now) / 60_000)
  return minutes > 0 ? minutes : null
}

/** "6 h 40 min" past the hour, "40 min" below it. */
export function formatDuration(minutes: number) {
  const hours = Math.floor(minutes / 60)
  if (hours === 0) return tTill("duration_minutes", { minutes })

  return tTill("duration_hours_minutes", { hours, minutes: minutes % 60 })
}

// ── The opening arqueo ───────────────────────────────────────────────────────
//
// One draft per counted dish, carrying the number the last close left behind so
// every question the row asks can be answered without reaching back for the
// stock list. Confirming is accepting `closingQuantity`; editing is replacing
// it. Both are decisions, and the difference between them is what the shift's
// signed line records — see `Location::ShiftStockCount`.

export type StockDraft = {
  itemStockId: string
  itemName: string
  closingQuantity: number
  value: string
  confirmed: boolean
}

export type StockDraftState = "pending" | "confirmed" | "edited"

export function initialStockDrafts(stocks: ItemStockRow[]): StockDraft[] {
  return stocks.map((stock) => ({
    itemStockId: stock.id,
    itemName: stock.itemName,
    closingQuantity: stock.quantity,
    value: String(stock.quantity),
    confirmed: false,
  }))
}

/**
 * Whole numbers only, blank included as invalid. A cashier midway through
 * clearing a field has not said "cero" — and `Number("")` is 0 in JavaScript,
 * which is exactly how a dish comes off the menu by accident.
 */
export function isValidStockValue(value: string) {
  return /^\d+$/.test(value.trim())
}

export function stockDraftState(draft: StockDraft): StockDraftState {
  if (!draft.confirmed) return "pending"

  return Number(draft.value) === draft.closingQuantity ? "confirmed" : "edited"
}

/**
 * Typing a different number IS the decision, so the row resolves itself — there
 * is no confirm-what-you-just-typed second tap. Typing back to the closing
 * number leaves an already-resolved row resolved: the cashier looked at it
 * either way. Clearing the field un-resolves it, because a blank is not a count.
 */
export function withStockValue(drafts: StockDraft[], itemStockId: string, value: string): StockDraft[] {
  return drafts.map((draft) => {
    if (draft.itemStockId !== itemStockId) return draft

    const valid = isValidStockValue(value)
    const changed = valid && Number(value) !== draft.closingQuantity

    return { ...draft, value, confirmed: valid && (draft.confirmed || changed) }
  })
}

export function confirmStockDraft(drafts: StockDraft[], itemStockId: string): StockDraft[] {
  return drafts.map((draft) =>
    draft.itemStockId === itemStockId && isValidStockValue(draft.value)
      ? { ...draft, confirmed: true }
      : draft
  )
}

/** Resolves every unconfirmed row at the number it already shows. */
export function confirmAllStockDrafts(drafts: StockDraft[]): StockDraft[] {
  return drafts.map((draft) => (isValidStockValue(draft.value) ? { ...draft, confirmed: true } : draft))
}

export function pendingStockDrafts(drafts: StockDraft[]) {
  return drafts.filter((draft) => stockDraftState(draft) === "pending").length
}

export function stockDraftsReady(drafts: StockDraft[]) {
  return pendingStockDrafts(drafts) === 0
}

/** Snake_case on the way out: this is a Rails payload, not a prop. */
export function stockCountPayload(drafts: StockDraft[]) {
  return drafts.map((draft) => ({ item_stock_id: draft.itemStockId, quantity: draft.value.trim() }))
}
